/**
 * Header-name-based Excel import for Product BOM spreadsheet.
 * Resolves columns from the real header row (e.g. "TÊN CHI TIẾT", "SL 1SP",
 * "VENEER") instead of fixed indices — avoids collisions like qty_per ← veneer.
 *
 * Factory exports use a single header row. Shop templates often use a multi-row
 * header: group labels ("KT TINH" / "KT THÔ") on the trigger row, real
 * DÀY/RỘNG/DÀI on the next row (repeated under each group; first match wins),
 * and sometimes a third hidden/junk header that also contains "Tên Chi Tiết" —
 * that junk row must not rebuild the column map.
 */
import type {
  BomSpreadsheetHardwareRow,
  BomSpreadsheetWoodRow,
} from './types/bomSpreadsheetTypes'
import { createBlankHardwareRow, createBlankWoodRow } from './types/bomSpreadsheetTypes'

export type WoodImportField = keyof BomSpreadsheetWoodRow
export type HwImportField = keyof BomSpreadsheetHardwareRow

/** Normalize header cell text for matching (upper, collapse space, strip accents loosely). */
export function normalizeBomExcelHeader(raw: unknown): string {
  return String(raw ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
}

/**
 * Map one wood-table header cell to a field.
 * Order matters: more specific labels (SL 1SP, VENEER) before generic ones.
 */
export function matchWoodHeaderField(header: string): WoodImportField | null {
  const h = normalizeBomExcelHeader(header)
  if (!h || h === 'STT') return null
  if (h.includes('M3') || h.includes('KHỐI LƯỢNG') || h.includes('KHOI LUONG')) return null

  if (h.includes('TÊN CHI TIẾT') || h.includes('TEN CHI TIET')) return 'part_name'
  if (h.includes('LOẠI GỖ') || h.includes('LOAI GO') || h.includes('PHÔI') || h.includes('PHOI')) {
    return 'wood_type_raw'
  }
  if (h === 'DÀY' || h === 'DAY' || h.startsWith('DÀY ') || h.startsWith('DAY ')) {
    return 'dims_finished_t'
  }
  if (h === 'RỘNG' || h === 'RONG' || h.startsWith('RỘNG ') || h.startsWith('RONG ')) {
    return 'dims_finished_w'
  }
  if (h === 'DÀI' || h === 'DAI' || h.startsWith('DÀI ') || h.startsWith('DAI ')) {
    return 'dims_finished_l'
  }
  if (h.includes('MỘNG') || h.includes('MONG') || h.includes('TENON')) return 'tenon'
  if (
    h.includes('SL TỔNG') ||
    h.includes('SL TONG') ||
    h.includes('SỐ LƯỢNG TỔNG') ||
    h.includes('SO LUONG TONG')
  ) {
    return 'qty_total'
  }
  // Shop templates use plain "SL" (exact); check after SL TỔNG so totals win.
  if (h === 'SL') return 'qty_per'
  if (h.includes('SL 1SP') || h.includes('SỐ LƯỢNG 1SP') || h.includes('SO LUONG 1SP')) {
    return 'qty_per'
  }
  if (h.includes('VENEER')) return 'veneer_spec'
  if (h.includes('GHI CHÚ') || h.includes('GHI CHU')) return 'notes'
  return null
}

export function matchHardwareHeaderField(header: string): HwImportField | null {
  const h = normalizeBomExcelHeader(header)
  if (!h || h === 'STT') return null

  if (
    h.includes('TÊN VẬT TƯ') ||
    h.includes('TEN VAT TU') ||
    h.includes('QUY CHUẨN') ||
    h.includes('QUY CHUAN')
  ) {
    return 'name_raw'
  }
  if (h.includes('QUY CÁCH') || h.includes('QUY CACH')) return 'spec_raw'
  if (
    h.includes('SL TỔNG') ||
    h.includes('SL TONG') ||
    h.includes('TỔNG LÔ') ||
    h.includes('TONG LO')
  ) {
    return 'qty_total'
  }
  if (h === 'SL' || h.includes('SỐ LƯỢNG') || h.includes('SO LUONG')) return 'qty_per'
  if (h.includes('ĐVT') || h.includes('DVT') || h.includes('ĐƠN VỊ') || h.includes('DON VI')) {
    return 'uom_hint'
  }
  if (h.includes('MÀU') || h.includes('MAU')) return 'color_hint'
  if (h.includes('PHÂN LOẠI') || h.includes('PHAN LOAI')) return 'item_type'
  if (h.includes('GHI CHÚ') || h.includes('GHI CHU') || h.includes('VỊ TRÍ') || h.includes('VI TRI')) {
    return 'notes'
  }
  return null
}

export function buildColumnMap<T extends string>(
  headerRow: unknown[],
  match: (header: string) => T | null,
): Partial<Record<T, number>> {
  const map: Partial<Record<T, number>> = {}
  headerRow.forEach((cell, idx) => {
    const field = match(String(cell ?? ''))
    if (field && map[field] === undefined) {
      map[field] = idx
    }
  })
  return map
}

/**
 * Merge column maps from multiple header candidate rows (trigger + optional
 * sub-header). Only fills fields still missing — first match wins across rows
 * and within each row (so DÀY under KT TINH wins over DÀY under KT THÔ).
 */
export function buildColumnMapFromHeaderRows<T extends string>(
  headerRows: Array<unknown[] | undefined | null>,
  match: (header: string) => T | null,
): Partial<Record<T, number>> {
  const map: Partial<Record<T, number>> = {}
  for (const row of headerRows) {
    if (!row) continue
    row.forEach((cell, idx) => {
      const field = match(String(cell ?? ''))
      if (field && map[field] === undefined) {
        map[field] = idx
      }
    })
  }
  return map
}

/** True when a row is a dimension sub-header (DÀY/RỘNG/DÀI labels), not data. */
export function looksLikeBomDimSubHeaderRow(row: unknown[]): boolean {
  let dimLike = 0
  for (const cell of row) {
    const h = normalizeBomExcelHeader(cell)
    if (
      h === 'DÀY' ||
      h === 'DAY' ||
      h === 'RỘNG' ||
      h === 'RONG' ||
      h === 'DÀI' ||
      h === 'DAI'
    ) {
      dimLike++
    }
  }
  return dimLike >= 2
}

function cellAt(row: unknown[], col: number | undefined): string {
  if (col === undefined) return ''
  return String(row[col] ?? '').trim()
}

function parseOptionalNumber(raw: string): number | string {
  if (!raw) return ''
  const n = parseFloat(raw)
  return Number.isFinite(n) ? n : ''
}

function isLikelySubHeaderName(name: string): boolean {
  return /^(dày|rộng|dài|day|rong|dai|quy cách|quy cach)$/i.test(name.trim())
}

export function parseWoodRowFromMappedColumns(
  row: unknown[],
  colMap: Partial<Record<WoodImportField, number>>,
): BomSpreadsheetWoodRow | null {
  const part_name = cellAt(row, colMap.part_name)
  if (!part_name || isLikelySubHeaderName(part_name)) return null

  const blank = createBlankWoodRow()
  return {
    ...blank,
    part_name,
    wood_type_raw: cellAt(row, colMap.wood_type_raw),
    dims_finished_t: parseOptionalNumber(cellAt(row, colMap.dims_finished_t)),
    dims_finished_w: parseOptionalNumber(cellAt(row, colMap.dims_finished_w)),
    dims_finished_l: parseOptionalNumber(cellAt(row, colMap.dims_finished_l)),
    tenon: cellAt(row, colMap.tenon),
    qty_per: parseOptionalNumber(cellAt(row, colMap.qty_per)),
    qty_total: parseOptionalNumber(cellAt(row, colMap.qty_total)),
    notes: cellAt(row, colMap.notes),
    veneer_spec: cellAt(row, colMap.veneer_spec),
  }
}

export function parseHardwareRowFromMappedColumns(
  row: unknown[],
  colMap: Partial<Record<HwImportField, number>>,
): BomSpreadsheetHardwareRow | null {
  const name_raw = cellAt(row, colMap.name_raw)
  if (!name_raw || isLikelySubHeaderName(name_raw)) return null

  const blank = createBlankHardwareRow()
  const uom = cellAt(row, colMap.uom_hint)
  const itemType = cellAt(row, colMap.item_type)
  return {
    ...blank,
    name_raw,
    spec_raw: cellAt(row, colMap.spec_raw),
    qty_per: parseOptionalNumber(cellAt(row, colMap.qty_per)),
    qty_total: parseOptionalNumber(cellAt(row, colMap.qty_total)),
    uom_hint: uom || 'Cái',
    color_hint: cellAt(row, colMap.color_hint),
    item_type: itemType || 'SX',
    notes: cellAt(row, colMap.notes),
  }
}

export function parseBomSpreadsheetExcelRows(jsonRows: unknown[][]): {
  wood: BomSpreadsheetWoodRow[]
  hardware: BomSpreadsheetHardwareRow[]
} {
  const wood: BomSpreadsheetWoodRow[] = []
  const hardware: BomSpreadsheetHardwareRow[] = []
  let mode: 'none' | 'wood' | 'hw' = 'none'
  let woodColMap: Partial<Record<WoodImportField, number>> = {}
  let hwColMap: Partial<Record<HwImportField, number>> = {}

  for (let i = 0; i < jsonRows.length; i++) {
    const raw = jsonRows[i]
    const r = (raw ?? []) as unknown[]
    const lineStr = r.map((c) => String(c ?? '')).join(' ').toUpperCase()
    const nextRow = (jsonRows[i + 1] ?? null) as unknown[] | null

    if (lineStr.includes('TÊN CHI TIẾT') || lineStr.includes('TEN CHI TIET')) {
      // Shop files often have a 3rd hidden/junk header that also contains
      // "Tên Chi Tiết". Once woodColMap is resolved for this wood section,
      // do NOT rebuild (would overwrite good KT TINH dims with wrong labels).
      if (mode === 'wood' && woodColMap.part_name !== undefined) {
        continue
      }
      mode = 'wood'
      woodColMap = buildColumnMapFromHeaderRows([r, nextRow ?? undefined], matchWoodHeaderField)
      if (nextRow && looksLikeBomDimSubHeaderRow(nextRow)) {
        i++ // skip sub-header so it is not treated as data
      }
      continue
    }
    if (
      lineStr.includes('VẬT TƯ SẢN XUẤT') ||
      lineStr.includes('VAT TU SAN XUAT') ||
      lineStr.includes('TÊN VẬT TƯ') ||
      lineStr.includes('TEN VAT TU')
    ) {
      if (mode === 'hw' && hwColMap.name_raw !== undefined) {
        continue
      }
      mode = 'hw'
      hwColMap = buildColumnMapFromHeaderRows(
        [r, nextRow ?? undefined],
        matchHardwareHeaderField,
      )
      if (nextRow && looksLikeBomDimSubHeaderRow(nextRow)) {
        i++
      }
      continue
    }
    if (
      lineStr.includes('TỔNG CỘNG') ||
      lineStr.includes('TONG CONG') ||
      lineStr.includes('NGƯỜI LẬP') ||
      lineStr.includes('NGUOI LAP')
    ) {
      mode = 'none'
      woodColMap = {}
      hwColMap = {}
      continue
    }

    if (mode === 'wood') {
      const parsed = parseWoodRowFromMappedColumns(r, woodColMap)
      if (parsed) wood.push(parsed)
    } else if (mode === 'hw') {
      const parsed = parseHardwareRowFromMappedColumns(r, hwColMap)
      if (parsed) hardware.push(parsed)
    }
  }

  return { wood, hardware }
}
